
import { mapGetters, mapActions } from 'vuex'
import Loading from '~/components/loading/Loading'
import blog from '~/mixins/blog'

export default {
  name: 'BlogCollection',
  components: { Loading },
  mixins: [blog],
  props: {
    source: {
      type: String,
      required: true
    },
    tags: {
      type: Array,
      default: () => []
    },
    limit: {
      type: String,
      default: '5'
    },
    showType: {
      type: String,
      default: 'list'
    },
    includeSelf: {
      type: Boolean,
      default: () => false
    },
    includeIndex: {
      type: Boolean,
      default: () => false
    },
    showDate: {
      type: Boolean,
      default: () => false
    },
    showTags: {
      type: Boolean,
      default: () => false
    },
    showAuthor: {
      type: Boolean,
      default: () => false
    },
    showPagination: {
      type: Boolean,
      default: () => false
    },
    pageMeta: { // Meta data about the page component this is a part of
      type: Object,
      required: true
    },
    sortBy: {
      type: String,
      required: true
    },
    excludeFromFull: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      stories: [],
      storiesCount: 0,
      zeroResult: false
    }
  },
  fetchOnServer: false,
  async fetch () {
    await this.getStories()
  },
  computed: {
    ...mapGetters({
      getLinkForUuid: 'storyblok/getLinkForUuid',
      currentLanguageCode: 'frontend/currentLanguageCode'
    }),
    sourceLinkFolder () {
      const item = this.getLinkForUuid(this.source)
      if (!item) {
        return ''
      } else if (!item.is_startpage) {
        const path = item.slug.split('/')
        path.pop()
        return path.join('/') + '/'
      } else {
        return item.slug
      }
    },
    queryParams () {
      const output = this.$route.query || {}
      if (!output.page) {
        output.page = 1
      } else {
        output.page = parseInt(output.page)
      }
      return output
    },
    // make sure we don't show a full type if this is a (grand)child of another full
    showTypeOut () {
      return !this.pageMeta?.hasFullBlogCollection ? this.showType : 'list'
    },
    topClasses () {
      const out = this.excludeFromFull.map((item) => {
        return `exclude-${item}`
      })
      out.push(this.showTypeOut)
      return out
    }
  },
  // Watches values that forces reload
  // mostly used for storyblok interface
  watch: {
    sortBy () {
      this.getStories()
    },
    source () {
      this.getStories()
    },
    tags () {
      this.getStories()
    },
    limit () {
      this.getStories()
    },
    includeSelf () {
      this.getStories()
    },
  },
  methods: {
    ...mapActions({
      fetchStories: 'storyblok/fetchStories'
    }),
    getStories () {
      this.zeroResult = false
      const params = {
        starts_with: this.currentLanguageCode + '/' + this.sourceLinkFolder
      }

      // Something is strange with the storyblok preview
      if (this.$route.query?._storyblok_lang) {
        params.starts_with = this.sourceLinkFolder
      }

      let perPage = parseInt(this.limit)
      if (perPage > 100 || perPage < 1) {
        perPage = 100
      }
      if (this.showType === 'summary-compact') {
        perPage = 4
      }
      params.per_page = perPage

      // Excludes current page
      // Type full always excludes self to prevent recursion
      if (!this.includeSelf || this.showType === 'full') {
        params.excluding_ids = [this.pageMeta.id]
      }

      // Removes indexpage
      if (!this.includeIndex) {
        params.is_startpage = 0
      }

      // Select page

      let tags = this.tags
      // tags
      if (this.queryParams.alltags) {
        tags = []
      } else if (this.queryParams.tag) {
        tags = this.queryParams.tag.split(',')
      }

      if (tags.length > 0) {
        params.with_tag = tags
      }

      // Page
      params.page = this.$route.query?.page || 1

      /**
         * Note for future editing
         * I've tried to sort by name
         * But SB only sort by default name
         * and not localized
         */
      params.sort_by = this.sortBy

      return this.fetchStories(params).then((response) => {
        if (response.data.stories.length === 0) {
          this.zeroResult = true
        }

        this.stories = response.data.stories
        this.storiesCount = parseInt(response.headers.total)
      }).catch((e) => {
        console.error(e)
      })
    },
    /**
     * Getting multiple stories from storyblok instead of just one
     * adds language code to link
     * @param {string} link
     * @return {string}
     */
    localLink (link) {
      if (link.substr(0, this.currentLanguageCode.length) === this.currentLanguageCode) {
        link = link.substr(this.currentLanguageCode.length + 1)
      }
      return this.$u(link)
    }
  }
}
